import { useParams, useSearchParams } from 'react-router-dom'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import InlineTipError from '@/components/inline-tips/InlineTipError'
import { sanitizeProviderHtml } from '@/content-viewer/util/sanitize-and-highlight-html'
import { useTitle } from '@/hooks/use-title'
import { kPaxtonName } from '@/constants/constants-strings'
import { FileDownload } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { useLrrMetadataInStandalone } from '../hooks/useLrrMetadataInStandalone'
import { useLrrHtmlInStandalone } from '../hooks/useLrrHtmlInStandalone'
import { useDownloadLrrAsDocx } from '../hooks/useDownloadLrrAsDocx'
import { useLrrContentStatusInStandalone } from '../hooks/useLrrContentStatusInStandalone'
import { LrrContentStatusInfoTag } from '@/agent-v2/references/LrrContentStatusInfoTag'
import { isDisplayableLrrContentStatus } from '@/agent-v2/references/lrr-content-status'

/**
 * Standalone LRR Reference Viewer Page
 * Displays LRR source HTML with title and citation header.
 */
export default function StandaloneReferenceViewerLrrPage() {
  const { s3Path } = useParams<{ s3Path: string }>()
  const decodedS3Path = decodeURIComponent(s3Path ?? '')
  // Absent on links made before the id was threaded through, and on any view not opened
  // from a citation; both correctly yield no badge.
  const [searchParams] = useSearchParams()
  const contentStatus = useLrrContentStatusInStandalone(searchParams.get('referenceId'))
  const { downloadAsDocx, isDownloading } = useDownloadLrrAsDocx()

  const formatDate = (dateString: string): string | null => {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Data fetching
  const { data: metadata, isLoading: isMetadataLoading, isError: isMetadataError, refetch: refetchMetadata } = useLrrMetadataInStandalone(decodedS3Path)
  const { data: htmlContent, isLoading: isHtmlLoading, isError: isHtmlError, refetch: refetchHtml } = useLrrHtmlInStandalone(decodedS3Path)

  const isLoading = isMetadataLoading || isHtmlLoading
  const isError = isMetadataError || isHtmlError

  // Page title
  const pageTitle = metadata?.title ?? `${kPaxtonName} Reference Viewer`
  useTitle(pageTitle)

  const sanitizedHtml = htmlContent ? sanitizeProviderHtml(htmlContent) : ''

  return (
    <div className="grow flex flex-col h-full overflow-y-scroll pb-10 pt-8">
      {isLoading && (
        <div className="flex justify-center items-center flex-1">
          <CircularProgressContinuousSized size={25} thickness={5} />
        </div>
      )}

      {isError && !isLoading && (
        <div className="pl-2">
          {InlineTipError('Could not load this reference.')}
          <div>
            <Button
              variant="filled"
              size="sm"
              onClick={() => {
                if (isMetadataError) refetchMetadata()
                if (isHtmlError) refetchHtml()
              }}
            >
              Retry
            </Button>
          </div>
        </div>
      )}

      {!isLoading && !isError && (
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-4">
          {/* Header */}
          <div className="pl-2 flex flex-col gap-0.5">
            <div className="flex items-center justify-between gap-4">
              <h1 className="text-xl font-bold truncate">{metadata?.title}</h1>
              {htmlContent && (
                <Button
                  variant="tonal"
                  color="neutral"
                  size="sm"
                  trailingIcon={<FileDownload className="h-4 w-4" />}
                  loading={isDownloading}
                  onClick={() => downloadAsDocx(sanitizedHtml, metadata?.title ?? 'source')}
                >
                  Download
                </Button>
              )}
            </div>
            {metadata?.citation && <p className="text-base text-brand-neutral-600">{metadata.citation}</p>}
            {(metadata?.jurisdiction_code || metadata?.doc_type) && (
              <p className="text-base text-brand-neutral-600">
                {[metadata.jurisdiction_code, metadata.doc_type.charAt(0).toUpperCase() + metadata.doc_type.slice(1)].filter(Boolean).join(' \u2022 ')}
              </p>
            )}
            {(metadata?.currency || metadata?.effective_date) && (
              <div className="mt-2 text-xs text-brand-neutral-500 flex gap-4">
                {metadata.currency && formatDate(metadata.currency) && <span>Current as of {formatDate(metadata.currency)}</span>}
                {metadata.effective_date && formatDate(metadata.effective_date) && <span>Effective {formatDate(metadata.effective_date)}</span>}
              </div>
            )}
            {isDisplayableLrrContentStatus(contentStatus) && (
              <div className="mt-2">
                <LrrContentStatusInfoTag status={contentStatus} />
              </div>
            )}
          </div>

          {/* Document content */}
          {htmlContent && (
            <div className="view-container bg-white w-full border border-brand-neutral-200 p-4" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
          )}
        </div>
      )}
    </div>
  )
}
